import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/site";

const Line = ({ children, delay = 0, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col pt-[57px]" data-testid="hero-section">
      <div className="flex-1 relative m-3 md:m-5 border border-border overflow-hidden">
        {/* Parallax backdrop */}
        <motion.div style={{ y: imgY }} className="absolute inset-0 -z-0">
          <img
            src={IMAGES.coastSunset}
            alt="White 1987 VW Vanagon Syncro on a coastal bluff overlooking the ocean"
            className="w-full h-[115%] object-cover img-warm"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/55 via-background/10 to-transparent" />
        </motion.div>

        {/* Kinetic poster type */}
        <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 h-full flex flex-col justify-between px-6 md:px-12 py-10 md:py-14">
          <div className="flex justify-between items-start font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-foreground/70">
            <Line delay={0.9}>
              <p>Full-time 4WD — Viscous coupling</p>
            </Line>
            <Line delay={1.05}>
              <p className="text-right">Wolfsburg heritage<br />Est. 1987</p>
            </Line>
          </div>

          <div className="mt-auto mb-[9vh]">
            <Line delay={0.15}>
              <p className="font-editorial italic text-xl md:text-3xl text-secondary mb-2 md:mb-4" data-testid="hero-kicker">
                a collectible expedition machine
              </p>
            </Line>
            <h1 className="font-headings uppercase leading-[0.82] tracking-tight" data-testid="hero-title">
              <Line delay={0.3}>
                <span className="block text-[19vw] md:text-[13vw]">Vanagon</span>
              </Line>
              <Line delay={0.45}>
                <span className="block text-[19vw] md:text-[13vw] text-primary">Syncro</span>
              </Line>
            </h1>
          </div>
          <div className="flex flex-col items-start gap-1 ml-4 md:ml-16">
            <Line delay={0.65}>
              <p className="font-headings uppercase text-5xl md:text-7xl leading-none text-primary" style={{ WebkitTextStroke: "2px #1A1A18" }} data-testid="hero-subline">For sale</p>
            </Line>
            <Line delay={0.95}>
              <p className="font-mono text-sm md:text-base uppercase tracking-[0.25em] text-background bg-foreground border border-foreground px-3 py-2 mt-3 inline-block" data-testid="hero-price">$25,000 · 224,000 miles<br />Marin County, CA</p>
            </Line>
          </div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
            <ArrowDown className="w-5 h-5 text-foreground/60" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
